import { useQuery } from "@apollo/client";
import { compact, uniqBy } from "lodash";
import { useMemo } from "react";
import {
  GetActiveEngagementSurveyActionsQuery,
  GetActiveEngagementSurveyActionsQueryVariables,
} from "types/graphql-schema";

import { ComplianceProgramItemProgramLink } from "@apps/assessments/components/homepage-active-reviews";
import ComplianceProgramActionItemForAssessment from "@apps/programs/components/compliance-program-action-item-for-assessment";
import { sortByCompletedItemsLast } from "@apps/programs/components/compliance-program-actions";
import {
  ComplianceProgramActionAssessmentItemProps,
  getAssessmentItemsForTemplate,
} from "@apps/programs/helpers";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import AppPopover from "@components/popover/app-popover";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getActiveEngagementSurveyActionsQuery from "../graphql/get-active-engagement-survey-actions-query";

const EngagementSurveyHomepageActiveSurveys = () => {
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();

  const { data, loading } = useQuery<
    GetActiveEngagementSurveyActionsQuery,
    GetActiveEngagementSurveyActionsQueryVariables
  >(getActiveEngagementSurveyActionsQuery, {
    variables: {
      organizationId: currentOrganization.id,
      currentUserId: currentUser.id,
    },
  });

  const compliancePrograms = useMemo(
    () =>
      data?.compliancePrograms
        ? assertEdgesNonNull(data.compliancePrograms)
        : [],
    [data]
  );

  const engagementSurveyCompliancePrograms = useMemo(
    () =>
      compliancePrograms.filter((program) => program.engagementSurveyTemplate),
    [compliancePrograms]
  );
  const engagementSurveyItems = engagementSurveyCompliancePrograms.reduce(
    (memo, program) => {
      if (!program.engagementSurveyTemplate) {
        return memo;
      }
      const items = getAssessmentItemsForTemplate(
        program,
        program.engagementSurveyTemplate,
        currentUser.id
      );
      return memo.concat(items);
    },
    [] as ComplianceProgramActionAssessmentItemProps[]
  );

  const reportIds = useMemo(
    () =>
      assertEdgesNonNull(currentUser.directReports).map((report) => report.id),
    [currentUser.directReports]
  );
  const hasReports = reportIds.length > 0;

  const myItemsCount = engagementSurveyItems.length;

  const programs: { id: number; title: string }[] = uniqBy(
    compact([...engagementSurveyCompliancePrograms]),
    "id"
  );

  return (
    <>
      <Layout.MainSection
        title="My Surveys"
        className="mt-8"
        rightSide={
          (isAdmin || hasReports) && (
            <AppPopover
              options={{
                className: classNames(
                  AppPopover.className,
                  "w-96 overflow-y-auto max-h-64"
                ),
              }}
              placement="bottom-end"
              content={
                <div className="p-4">
                  <div className="text-sm font-medium mb-2">Programs</div>
                  <div className="flex flex-col divide-y border-t border-b">
                    {programs.map((program) => (
                      <AppLink
                        key={program.id}
                        to={`/programs/${program.id}/progress`}
                        className="py-2 px-1 text-sm text-gray-500 hover:bg-gray-50 flex items-center justify-between"
                      >
                        {program.title}
                        <TopicflowIcon type="rightArrow" className="w-4 h-4" />
                      </AppLink>
                    ))}
                  </div>
                </div>
              }
            >
              <AppPopover.Button
                as={Button}
                className="flex items-center gap-1"
                disabled={programs.length === 0}
              >
                View program progress{" "}
                <TopicflowIcon
                  type="chevronDown"
                  className="w-3 h-3 text-gray-500"
                />
              </AppPopover.Button>
            </AppPopover>
          )
        }
      >
        <Layout.MainSubSection
          title=""
          loading={loading}
          emptyPlaceholder={
            myItemsCount === 0 ? `There are no active Surveys for you` : null
          }
        >
          <Layout.MainSubSectionList>
            {sortByCompletedItemsLast(engagementSurveyItems).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              >
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItemForAssessment>
            ))}
          </Layout.MainSubSectionList>
        </Layout.MainSubSection>
      </Layout.MainSection>
    </>
  );
};

export default EngagementSurveyHomepageActiveSurveys;
